import { VideoPlayer } from "~/components/VideoPlayer"
import {
  CheckCircleOutlined,
  CopyOutlined,
  DownOutlined,
  EditOutlined,
  EyeOutlined,
} from "@ant-design/icons"
import { useNavigate } from "react-router"
import { getTaskListPath } from "~/common/router"
import { useMemo, useState } from "react"
import { Button, Flex, Input, Typography, message } from "antd"
import { Alert } from "~/components/Alert"
import { useShallow } from "zustand/react/shallow"
import {
  AnalyticsAction,
  Text,
  VideoGenerateFooter,
  useAnalyticsEvent,
} from "~/components"
import { getVideoDurationRange, VIDEO_TOOL_LABELS } from "~/common/constants"
import { copyText } from "~/common/utils"
import {
  getUploadedResourceMaterialLabel,
  type UploadedResource,
} from "~/common/media/uploadedResources"
import {
  formatVideoAnalysisPromptText,
  validateShotTimeline,
  validateStoryboardContent,
} from "~/common/video/storyboard"
import {
  useHotVideoStore,
  isHotVideoPromptStale,
  getHotVideoGenerationOptions,
} from "~/store/hotVideo"
import {
  getRecreateGenerationError,
  getRecreateGenerationResources,
  getRecreateReferenceChanges,
} from "~/common/video/recreate"
import { getVideoModelSelectionError } from "~/common/video/videoModels"
import { SavedStoryboardEditor } from "./SavedStoryboardEditor"
import { ScriptSaveStatus } from "./ScriptSaveStatus"
import { VideoSoundSettings } from "./VideoSoundSettings"
import { getExampleVideos } from "./helpers"
import { useVideoTemplates } from "~/store/videoTemplates"
import styles from "./index.css"

const { Title } = Typography

export const ResultPanel = ({
  taskListPath = getTaskListPath("recreate"),
}: {
  taskListPath?: string | ((taskId: string) => string)
}) => {
  const { templates } = useVideoTemplates()
  const exampleVideos = getExampleVideos(templates)

  const navigate = useNavigate()
  const sendAnalytics = useAnalyticsEvent({
    category: "爆款复刻页",
    prefix: "爆款复刻",
  })
  const onOpenTaskList = () =>
    navigate(
      typeof taskListPath === "function"
        ? taskListPath(useHotVideoStore.getState().taskId)
        : taskListPath,
    )
  const {
    scriptRevision,
    canSaveScript,
    hasEditedScript,
    taskErrorMessage,
    hasReplicaPrompt,
    replicaPrompt,
    isRequesting,
    rawModelOutput,
    replicaStoryboardRows,
    showStoryboardValidation,
    sourcePrompt,
    isPromptStale,
    isModelChanged,
    isSubmittingVideoTask,
    status,
    generateReplicaVideo,
    validateReplicaVideo,
    updateRawModelOutput,
    updateReplicaStoryboardRows,
    saveReplicaPrompt,
  } = useHotVideoStore(
    useShallow((state) => ({
      scriptRevision: state.scriptRevision,
      canSaveScript: state.canSaveScript,
      hasEditedScript: state.hasEditedScript,
      taskErrorMessage: state.taskErrorMessage,
      hasReplicaPrompt: Boolean(state.replicaPrompt.trim()),
      replicaPrompt: state.replicaPrompt,
      isRequesting: state.isRequesting,
      rawModelOutput: state.rawModelOutput,
      replicaStoryboardRows: state.replicaStoryboardRows,
      showStoryboardValidation: state.showStoryboardValidation,
      sourcePrompt: state.sourcePrompt,
      isPromptStale: isHotVideoPromptStale(state),
      isModelChanged: getRecreateReferenceChanges(state).includes("模特"),
      isSubmittingVideoTask: state.isSubmittingVideoTask,
      status: state.status,
      generateReplicaVideo: state.generateReplicaVideo,
      validateReplicaVideo: state.validateReplicaVideo,
      updateRawModelOutput: state.updateRawModelOutput,
      updateReplicaStoryboardRows: state.updateReplicaStoryboardRows,
      saveReplicaPrompt: state.saveReplicaPrompt,
    })),
  )
  const onGenerate = async () => {
    await generateReplicaVideo(sendAnalytics)
  }
  const referenceError = useHotVideoStore(getRecreateGenerationError)
  const [sourcePromptExpanded, setSourcePromptExpanded] = useState(false)
  const [generateConfirmOpen, setGenerateConfirmOpen] = useState(false)
  const hasStoryboard = replicaStoryboardRows.length > 0
  const videoOptions = useHotVideoStore(
    useShallow(getHotVideoGenerationOptions),
  )
  const {
    setVideoOptions,
    videoImageUrls,
    referenceModel,
    savedReferenceModel,
    referenceAudio,
  } = useHotVideoStore(
    useShallow((state) => ({
      setVideoOptions: state.setVideoOptions,
      videoImageUrls: state.videoImageUrls,
      referenceModel: state.referenceModel,
      savedReferenceModel: state.savedReferenceModel,
      referenceAudio: state.referenceAudio,
    })),
  )
  const mentionableResources = useMemo<UploadedResource[]>(
    () =>
      getRecreateGenerationResources({
        videoImageUrls,
        referenceModel,
        savedReferenceModel,
        referenceAudio,
        replicaPrompt,
      }),
    [
      videoImageUrls,
      referenceModel,
      savedReferenceModel,
      referenceAudio,
      replicaPrompt,
    ],
  )
  const referenceImageCount = mentionableResources.filter((resource) =>
    resource.type?.startsWith("image/"),
  ).length
  const videoDurationRange = getVideoDurationRange(videoOptions.model)
  const { visualErrorShotNumbers } = useMemo(
    () =>
      validateStoryboardContent(
        showStoryboardValidation ? replicaStoryboardRows : [],
      ),
    [replicaStoryboardRows, showStoryboardValidation],
  )
  const storyboardTimeErrorShotNumbers = useMemo(() => {
    if (!showStoryboardValidation) return []

    return validateShotTimeline(
      replicaStoryboardRows.map((row) => ({
        endTime: row.end_time,
        id: row.shot_number,
        startTime: row.start_time,
      })),
      videoOptions.model,
    ).timeErrorIds
  }, [replicaStoryboardRows, showStoryboardValidation, videoOptions.model])
  const showInitialGuide =
    !isRequesting &&
    !taskErrorMessage &&
    !hasStoryboard &&
    !rawModelOutput.trim() &&
    !hasReplicaPrompt &&
    !canSaveScript &&
    !hasEditedScript
  const showResultFooter =
    hasReplicaPrompt && !isRequesting && !isSubmittingVideoTask
  const sourcePromptText = useMemo(() => {
    return formatVideoAnalysisPromptText(sourcePrompt)
  }, [sourcePrompt])
  const copySourcePrompt = async () => {
    const copied = await copyText(sourcePromptText)
    if (copied) {
      void message.success("复制成功")
    } else {
      void message.error("复制失败，请手动复制")
    }
  }
  const materialType = getUploadedResourceMaterialLabel(mentionableResources)
  const referenceChangeNotice =
    "参考素材或商品信息已修改，已有脚本不会自动重新生成，请确认当前脚本与预期一致后继续生成视频。" +
    (isModelChanged
      ? "模特选择不会自动同步，本次仍使用任务中已保存的模特或线稿图。"
      : "")
  const disabledReason =
    referenceError ||
    getVideoModelSelectionError(videoOptions.model, {
      resources: mentionableResources,
      duration: videoOptions.duration,
    })

  return (
    <Flex
      vertical
      className={styles.resultPanel}
    >
      {taskErrorMessage ? (
        <Alert
          description={taskErrorMessage}
          showIcon
          title="任务执行失败"
          level="strong"
        />
      ) : null}
      {sourcePrompt ? (
        <Flex
          className={styles.sourcePromptSection}
          vertical
        >
          <Flex
            align="center"
            className={styles.sourcePromptHeader}
          >
            <AnalyticsAction
              action={() => !sourcePromptExpanded && "查看原视频分镜脚本"}
              prefix="爆款复刻"
            >
              <button
                aria-expanded={sourcePromptExpanded}
                className={styles.sourcePromptToggle}
                type="button"
                onClick={() => setSourcePromptExpanded((current) => !current)}
              >
                <Flex
                  component="span"
                  align="center"
                  gap={8}
                >
                  <span
                    className={`${styles.sectionDot} ${styles.sourceDot}`}
                  />
                  <Text
                    strong
                    color="inherit"
                  >
                    参考视频脚本拆解结果
                  </Text>
                  <span className={styles.toggleIcon}>
                    <DownOutlined rotate={sourcePromptExpanded ? 0 : -90} />
                  </span>
                  <Text
                    size="s"
                    color="var(--ant-color-text-tertiary)"
                    className={styles.referenceBadge}
                  >
                    仅供对照参考
                  </Text>
                </Flex>
              </button>
            </AnalyticsAction>
            <AnalyticsAction
              action="复制原视频分镜脚本"
              prefix="爆款复刻"
            >
              <Button
                className={styles.sourcePromptCopy}
                disabled={!sourcePromptText.trim()}
                icon={<CopyOutlined />}
                onClick={copySourcePrompt}
              >
                复制
              </Button>
            </AnalyticsAction>
          </Flex>
          {sourcePromptExpanded ? (
            <Flex
              className={styles.sourceAnalysis}
              vertical
              gap={8}
            >
              <Text
                color="primary"
                strong
              >
                <EyeOutlined /> 参考本结果校对 AI 生成脚本 —— 滚动查看完整内容
              </Text>
              <Text className={styles.sourceAnalysisText}>
                {sourcePromptText}
              </Text>
            </Flex>
          ) : null}
        </Flex>
      ) : null}
      {!showInitialGuide && (
        <Flex
          className={styles.scriptHeader}
          align="center"
          justify="space-between"
          gap={12}
        >
          <Flex
            align="center"
            gap={8}
          >
            <span className={styles.sectionDot} />
            <Text
              size="m"
              className={styles.sectionHeading}
            >
              AI 生成新视频脚本
            </Text>
            <Text
              size="s"
              color="primary"
              className={styles.draftBadge}
            >
              初稿
            </Text>
          </Flex>
          {hasStoryboard ? (
            <Text
              size="s"
              className={styles.shotCount}
              type="secondary"
            >
              {replicaStoryboardRows.length} 个分镜
            </Text>
          ) : null}
        </Flex>
      )}

      <div className={styles.resultBody}>
        <ScriptSaveStatus />
        {isSubmittingVideoTask ? (
          <Flex
            className={styles.taskSubmittedState}
            align="center"
            justify="center"
            orientation="vertical"
            gap={16}
          >
            <CheckCircleOutlined className={styles.taskSubmittedIcon} />
            <Title
              level={5}
              type="secondary"
            >
              AI
              生视频任务已提交，预计需要消耗几分钟，稍后可在任务列表中查看结果
            </Title>
            <AnalyticsAction
              action="点击查看任务列表"
              prefix="生视频任务"
            >
              <Button
                type="primary"
                onClick={onOpenTaskList}
              >
                查看任务列表
              </Button>
            </AnalyticsAction>
          </Flex>
        ) : isRequesting ? (
          <Flex
            className={styles.processingState}
            align="center"
            justify="center"
            orientation="vertical"
            gap={8}
          >
            <div className={styles.loadingOrb}>
              <div className={styles.loadingOrbInner} />
            </div>
            <Title level={4}>{status || "AI 正在处理..."}</Title>
            <Text type="secondary">
              AI 融合生成新视频脚本需要一些时间，
              <Text type="secondary">
                你可关闭当前页，稍后可在
                <Text color="primary">
                  【任务列表】- 【{VIDEO_TOOL_LABELS.recreate}】
                </Text>
                查看任务进度
              </Text>
            </Text>
            <div className={styles.loadingBars}>
              <span className={styles.loadingBar} />
              <span className={styles.loadingBar} />
              <span className={styles.loadingBar} />
            </div>
          </Flex>
        ) : hasStoryboard ? (
          <Flex
            orientation="vertical"
            gap={16}
          >
            <Alert
              description="AI 生成内容可能会存在场景描述偏差、商品细节缺失、文案表述有误等问题。提交生成视频前请仔细核对每个分镜，确保内容与商品实际信息一致，避免积点浪费"
              showIcon
              title="以下脚本为 AI 初稿，提交 AI 生视频前请务必人工审核"
              level="strong"
            />
            <Flex
              className={styles.editHint}
              align="center"
              gap={8}
              wrap
            >
              <EditOutlined className={styles.editHintIcon} />
              <Text
                size="s"
                strong
              >
                每个分镜均可手动修改
              </Text>
              <Text
                size="s"
                type="secondary"
              >
                {canSaveScript
                  ? "——直接点击文字内容即可编辑，离开输入框后自动保存"
                  : "——直接点击文字内容即可编辑，修改内容将在生成视频时提交"}
              </Text>
            </Flex>
            <SavedStoryboardEditor
              key={scriptRevision}
              mentionableResources={mentionableResources}
              rows={replicaStoryboardRows}
              visualErrorShotNumbers={visualErrorShotNumbers}
              timeEditing={{
                errorShotNumbers: storyboardTimeErrorShotNumbers,
                max: videoDurationRange.MAX,
                min: videoDurationRange.MIN,
              }}
              onChange={updateReplicaStoryboardRows}
              onSave={() => void saveReplicaPrompt()}
            />
          </Flex>
        ) : rawModelOutput || canSaveScript || hasEditedScript ? (
          <Flex
            orientation="vertical"
            gap={12}
          >
            <Text
              className={styles.storyboardTitle}
              strong
            >
              新视频脚本初稿
            </Text>
            <Text
              size="s"
              className={styles.storyboardHint}
              type="secondary"
            >
              生成视频时会直接使用这段提示词，可按需编辑。
            </Text>
            <Input.TextArea
              className={styles.rawAnalysis}
              value={rawModelOutput}
              onChange={(event) => updateRawModelOutput(event.target.value)}
              onBlur={() => void saveReplicaPrompt()}
              autoSize={{ minRows: 10 }}
            />
          </Flex>
        ) : showInitialGuide ? (
          <Flex
            align="center"
            orientation="vertical"
            gap={8}
            className={styles.initialGuide}
            justify="center"
          >
            <Title
              className={styles.guideTitle}
              level={2}
            >
              AI智能复刻，3分钟生成带货爆款
            </Title>
            <Text type="secondary">
              只需上传爆款视频及商品图，AI自动拆解爆款逻辑并融合新卖点，一键生成专属带货视频
            </Text>
            <Flex
              className={styles.exampleVideos}
              gap={12}
              justify="center"
              wrap
            >
              {exampleVideos.map((item) => (
                <Flex
                  className={styles.exampleVideoPlaceholder}
                  align="center"
                  justify="center"
                  key={item.label}
                >
                  <VideoPlayer
                    autoPlay
                    className={styles.exampleVideo}
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    src={item.videoUrl}
                  />
                  <Text
                    size="s"
                    color="var(--ant-color-text-light-solid)"
                    className={styles.exampleVideoLabel}
                  >
                    {item.label}
                  </Text>
                </Flex>
              ))}
            </Flex>
          </Flex>
        ) : (
          <Flex
            className={styles.emptyResult}
            align="center"
            justify="center"
          />
        )}
      </div>

      {showResultFooter ? (
        <Flex
          className={styles.resultActions}
          vertical
          gap={8}
        >
          {isPromptStale ? (
            <Alert
              level="weak"
              showIcon
              title={referenceChangeNotice}
            />
          ) : null}
          <VideoGenerateFooter
            analytics={{
              action: "点击生成视频",
              fields: {
                materialType,
              },
              prefix: "爆款复刻",
            }}
            className={styles.resultFooter}
            durationReadOnly={hasStoryboard}
            disabled={Boolean(disabledReason)}
            disabledReason={disabledReason}
            generateConfirm={{
              open: generateConfirmOpen,
              onOpenChange: (open) => {
                setGenerateConfirmOpen(open && validateReplicaVideo())
              },
              description: isPromptStale ? referenceChangeNotice : undefined,
            }}
            loading={isRequesting}
            actions={<VideoSoundSettings />}
            onGenerate={onGenerate}
            onVideoOptionsChange={setVideoOptions}
            referenceImageCount={referenceImageCount}
            referenceResources={mentionableResources}
            videoOptions={videoOptions}
          />
        </Flex>
      ) : null}
    </Flex>
  )
}
